import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, X, Clock, Calculator, FileText, Layout, ArrowRight, Dumbbell, MapPin, ShoppingBag } from 'lucide-react';
import { articlesApi } from '../../lib/api';

const STATIC_PAGES = [
  { title: 'HYROX Hub', path: '/hyrox', type: 'page', description: 'Training guides, gym finder, tools & equipment' },
  { title: 'Blog', path: '/blog', type: 'page', description: 'Articles on biohacking, fitness & performance' },
  { title: 'Shop', path: '/shop', type: 'page', description: 'Performance gear and supplements' },
  { title: 'Recovery Locator', path: '/locator', type: 'page', description: 'Cold plunge, sauna, HBOT & peptide clinics near you' },
  { title: 'Gear Box Subscription', path: '/gear-box', type: 'page', description: 'Monthly performance gear delivered' },
  { title: 'Peptide Membership', path: '/peptide-subscription', type: 'page', description: 'Biohacker Network membership & rewards' },
  { title: 'About', path: '/about', type: 'page', description: 'The NinjAthlete story' },
  { title: 'Contact', path: '/contact', type: 'page', description: 'Get in touch' },
  { title: 'My Results Dashboard', path: '/my-results', type: 'page', description: 'Your saved calculator results' },
  { title: 'Affiliates', path: '/affiliates', type: 'page', description: 'Partner programs and discounts' },
];

const STATIC_TOOLS = [
  { title: 'HYROX Finish Time Predictor', path: '/tools/hyrox-finish-time', type: 'tool' },
  { title: 'Station Split & Race Pace Planner', path: '/tools/station-split', type: 'tool' },
  { title: 'Macro Calculator', path: '/tools/macro-calculator', type: 'tool' },
  { title: 'Peptide Dosing Calculator', path: '/tools/peptide-dosing', type: 'tool' },
  { title: 'Peptide Reconstitution Calculator', path: '/tools/peptide-calculator', type: 'tool' },
  { title: 'Protein Calculator', path: '/tools/protein-calculator', type: 'tool' },
  { title: 'TDEE & Calorie Calculator', path: '/tools/tdee-calculator', type: 'tool' },
  { title: 'One Rep Max Calculator', path: '/tools/one-rep-max', type: 'tool' },
  { title: 'Body Fat Estimator', path: '/tools/body-fat-calculator', type: 'tool' },
  { title: 'Heart Rate Zones', path: '/tools/heart-rate-zones', type: 'tool' },
  { title: 'Focus & Recovery Timers', path: '/timers', type: 'tool' },
];

const CATEGORY_PAGES = [
  { title: 'Meditation Articles', path: '/blog?category=Meditation', type: 'category', description: 'Mindfulness, breathwork & ancient practices' },
  { title: 'Biohacking Articles', path: '/blog?category=Biohacking', type: 'category', description: 'Cold exposure, supplements & optimization' },
  { title: 'Fitness Articles', path: '/blog?category=Fitness', type: 'category', description: 'Training, workouts & performance' },
  { title: 'Peptide Guides', path: '/blog?category=Peptides', type: 'category', description: 'Peptide research & protocols' },
  { title: 'Nutrition Articles', path: '/blog?category=Nutrition', type: 'category', description: 'Diet, supplements & meal planning' },
  { title: 'HYROX Articles', path: '/blog?category=HYROX', type: 'category', description: 'Training plans, race guides & gym directories' },
  { title: 'Wellness Articles', path: '/blog?category=Wellness', type: 'category', description: 'Recovery, sleep & holistic health' },
];

const TYPE_ICONS = {
  article: FileText,
  tool: Calculator,
  page: Layout,
  category: Layout,
  gym: Dumbbell,
  facility: MapPin,
  product: ShoppingBag,
};

const TYPE_COLORS = {
  article: '#E63946',
  tool: '#00F0FF',
  page: '#e8ff00',
  category: '#22c55e',
  gym: '#C7FA3E',
  facility: '#f59e0b',
  product: '#a78bfa',
};

const EMPTY_RESULTS = { articles: [], gyms: [], facilities: [], products: [] };

export const GlobalSearch = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(EMPTY_RESULTS);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const searchTimeout = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults(EMPTY_RESULTS);
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const searchSite = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults(EMPTY_RESULTS); return; }
    setLoading(true);
    try {
      const res = await articlesApi.siteSearch(q);
      setResults({ ...EMPTY_RESULTS, ...(res.data || {}) });
    } catch { setResults(EMPTY_RESULTS); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => searchSite(query), 250);
    return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
  }, [query, searchSite]);

  const q = query.toLowerCase().trim();
  const matchedTools = q.length >= 2 ? STATIC_TOOLS.filter(t => t.title.toLowerCase().includes(q)) : [];
  const matchedPages = q.length >= 2 ? [...STATIC_PAGES, ...CATEGORY_PAGES].filter(p =>
    p.title.toLowerCase().includes(q) || (p.description && p.description.toLowerCase().includes(q))
  ) : [];

  const groups = [
    { label: 'Pages', type: null, items: matchedPages.map(p => ({ ...p })) },
    { label: 'Tools', type: 'tool', items: matchedTools.map(t => ({ ...t })) },
    { label: 'Articles & Guides', type: 'article', items: results.articles.map(a => ({ ...a, type: 'article' })) },
    { label: 'Gyms & Directories', type: 'gym', items: results.gyms.map(g => ({ ...g, type: 'gym', description: g.context })) },
    { label: 'Recovery & Wellness', type: 'facility', items: results.facilities.map(f => ({ ...f, type: 'facility', description: f.context })) },
    { label: 'Gear', type: 'product', items: results.products.map(p => ({ ...p, type: 'product', description: p.context })) },
  ].filter(g => g.items.length > 0);

  const allResults = groups.flatMap(g => g.items);

  useEffect(() => { setSelectedIndex(0); }, [query]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(i => Math.min(i + 1, allResults.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && allResults[selectedIndex]) {
      e.preventDefault();
      navigate(allResults[selectedIndex].path);
      onClose();
    }
  };

  if (!isOpen) return null;

  let runningIndex = 0;

  return (
    <div className="fixed inset-0 z-[100]" data-testid="global-search-overlay">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative max-w-2xl mx-auto mt-20 md:mt-32 px-4">
        <div className="bg-zinc-900 border border-zinc-700 shadow-2xl overflow-hidden" data-testid="global-search-panel">
          {/* Search Input */}
          <div className="flex items-center px-5 border-b border-zinc-800">
            <Search className="w-5 h-5 text-zinc-500 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search gyms, guides, tools, gear..."
              className="flex-1 px-4 py-4 bg-transparent text-white text-base placeholder-zinc-500 focus:outline-none"
              data-testid="global-search-input"
            />
            <button onClick={onClose} className="p-1 text-zinc-500 hover:text-white transition-colors" data-testid="global-search-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Results */}
          <div className="max-h-[60vh] overflow-y-auto" data-testid="global-search-results">
            {/* eslint-disable-next-line no-nested-ternary -- readable tier picker */}
            {q.length < 2 ? (
              <div className="p-6 text-center">
                <p className="text-zinc-500 text-sm mb-4">Search articles, gyms, recovery clinics, tools & gear</p>
                <div className="flex flex-wrap justify-center gap-2">
                  {['HYROX', 'Dallas', 'Peptides', 'Recovery', 'Macro'].map(tag => (
                    <button key={tag} onClick={() => setQuery(tag)}
                      className="px-3 py-1.5 text-xs bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-500 hover:text-white transition-colors"
                      data-testid={`search-tag-${tag.toLowerCase()}`}>
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            // eslint-disable-next-line no-nested-ternary -- readable tier picker
            ) : loading && allResults.length === 0 ? (
              <div className="p-6 text-center text-zinc-500 text-sm">Searching...</div>
            ) : allResults.length === 0 ? (
              <div className="p-6 text-center text-zinc-500 text-sm">No results found for "{query}"</div>
            ) : (
              <div className="py-2">
                {groups.map((group) => (
                  <div key={group.label}>
                    <p className="px-5 py-2 text-[10px] uppercase tracking-wider text-zinc-600 font-heading">{group.label}</p>
                    {group.items.map((item) => {
                      const globalIdx = runningIndex;
                      runningIndex += 1;
                      return (
                        <ResultItem key={`${group.label}-${item.path}`} item={item} type={item.type || group.type}
                          isSelected={selectedIndex === globalIdx} onClose={onClose} />
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer hints */}
          <div className="px-5 py-3 border-t border-zinc-800 flex items-center gap-4 text-zinc-600 text-[10px] uppercase tracking-wider">
            <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-zinc-800 border border-zinc-700 text-zinc-400 rounded text-[10px]">ESC</kbd> Close</span>
            <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-zinc-800 border border-zinc-700 text-zinc-400 rounded text-[10px]">&uarr;&darr;</kbd> Navigate</span>
            <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-zinc-800 border border-zinc-700 text-zinc-400 rounded text-[10px]">Enter</kbd> Go</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const ResultItem = ({ item, type, isSelected, onClose }) => {
  const Icon = TYPE_ICONS[type] || FileText;
  const color = TYPE_COLORS[type] || '#E63946';
  return (
    <Link
      to={item.path}
      onClick={onClose}
      className={`flex items-center gap-3 px-5 py-3 transition-colors ${isSelected ? 'bg-zinc-800' : 'hover:bg-zinc-800/50'}`}
      data-testid={`search-result-${item.path.replace(/[/#?]/g, '-').slice(1)}`}
    >
      <div className="w-8 h-8 flex items-center justify-center border border-zinc-700 flex-shrink-0" style={{ backgroundColor: `${color}10` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm truncate">{item.title}</p>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider" style={{ color }}>{type}</span>
          {item.category && <span className="text-zinc-600 text-[10px]">{item.category}</span>}
          {item.read_time && <span className="text-zinc-600 text-[10px] flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{item.read_time} min</span>}
          {item.readTime && <span className="text-zinc-600 text-[10px] flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{item.readTime} min</span>}
          {item.description && <span className="text-zinc-600 text-[10px] truncate">{item.description}</span>}
        </div>
      </div>
      <ArrowRight className="w-3 h-3 text-zinc-600 flex-shrink-0" />
    </Link>
  );
};
